import { unref as vueUnref } from 'vue';
import Decimal               from 'decimal.js';
import type Qty              from "@/core/types/Qty";
import StringUtils           from '@/core/utils/StringUtils';
import Core                  from "@/core/Core";

class NumberUtils {
	/**
	 * Проверяет, является ли значение числом
	 */
	static isNumber(value: unknown): value is number {
		value = vueUnref(value);
		return typeof value === 'number' && !isNaN(value);
	}

	/**
	 * Проверяет, можно ли значение преобразовать в число
	 */
	static isNumberLike(value: unknown): value is number | string {
		value = vueUnref(value);
		return typeof value === 'number' || (typeof value === 'string' && !isNaN(Number(value)) && value.trim() !== '');
	}

	/**
	 * Преобразует значение в число
	 */
	static toNumber(value: unknown): number | null {
		value = vueUnref(value);
		if (!this.isNumberLike(value)) {
			return null;
		}
		return typeof value === 'number' ? value : Number(value);
	}

	/**
	 * Дополняет число ведущими нулями до заданной длины
	 * @param number - Число для форматирования
	 * @param length - Требуемая длина строки (по умолчанию 2)
	 */
	static leftPadZero(number: unknown, length: number = 2): string | null {
		return StringUtils.leftPad(number, length, '0');
	}

	/**
	 * Универсальная функция для корректировки десятичных операций
	 * @param type - Тип операции ('round', 'floor', 'ceil')
	 * @param value - Значение для обработки
	 * @param exp - Показатель степени (отрицательный для десятичных знаков)
	 */
	static decimalAdjust(type: 'round' | 'floor' | 'ceil', value: number, exp: number): number {
		if (typeof exp === 'undefined' || +exp === 0) {
			return Math[type](value);
		}
		value = +value;
		exp = +exp;
		if (isNaN(value) || !(typeof exp === 'number' && exp % 1 === 0)) {
			return NaN;
		}
		const valueArr = value.toString().split('e');
		const adjusted = Math[type](+(valueArr[0] + 'e' + (valueArr[1] ? +valueArr[1] - exp : -exp)));
		const adjustedArr = adjusted.toString().split('e');
		return +(adjustedArr[0] + 'e' + (adjustedArr[1] ? +adjustedArr[1] + exp : exp));
	}

	// Разница round10 и ceilPrecise
	// round10 - округляет к ближайшему (математическое)
	// ceilPrecise - округляет ВВЕРХ (к потолку)

	// round10(1.2345, -3)     // 1.235 (округление к ближайшему)
	// ceilPrecise(1.2345, 3)  // 1.235 (округление вверх)
	//
	// round10(1.2341, -3)     // 1.234 (округление вниз)
	// ceilPrecise(1.2341, 3)  // 1.235 (всегда вверх!)

	/**
	 * Округляет число до указанного количества десятичных знаков
	 * @param value - Значение для округления
	 * @param exp - Количество десятичных знаков (положительное число)
	 */
	static round10(value: number, exp: number): number {
		const localExp = exp < 0 ? exp : -exp;
		return this.decimalAdjust('round', value, localExp);
	}

	/**
	 * Проверяет, близки ли два числа с заданной точностью
	 * @param a - Первое число
	 * @param b - Второе число
	 * @param precision - Точность (по умолчанию 0.01)
	 */
	static isCloseTo(a: number, b: number, precision: number = 0.01): boolean {
		return a / b < 1 + precision && a / b > 1 - precision;
	}

	/**
	 * Вычисляет указанный процент от числа
	 * @param percentage - Процент (например, 20 для 20%)
	 * @param value - Значение, от которого вычисляется процент
	 * @param precision - Количество знаков после запятой (по умолчанию 2)
	 */
	static calculatePercentage(percentage: number, value: number, precision: number = 2): number {
		return this.round10((percentage / 100) * value, precision);
	}

	/**
	 * Вычисляет какой процент составляет часть от целого
	 * @param part - Часть от целого
	 * @param whole - Целое значение
	 * @param precision - Количество знаков после запятой (по умолчанию 2)
	 */
	static getPercentageOf(part: number, whole: number, precision: number = 2): number {
		if (whole === 0) {
			return 0;
		}
		return this.round10((part * 100) / whole, precision);
	}

	/**
	 * Находит пересечение массива числовых интервалов.
	 * Пропускает полностью пустые интервалы [null, null].
	 * Полуоткрытые интервалы [10, null] или [null, 25] сужают только ту границу, которая задана.
	 */
	static numberIntervalIntersection(
		intervals: Array<{ start: number; end: number } | null>
	): { start: number; end: number } | null {
		intervals = vueUnref(intervals);

		if (!Array.isArray(intervals)) return null;

		return intervals.reduce<{ start: number; end: number } | null>((result, item) => {
			// Пропускаем null и полностью пустые интервалы [null, null]
			if (item === null || (item.start === null && item.end === null)) return result;

			// Первый валидный интервал — копируем как базу
			if (result === null) return { start: item.start, end: item.end };

			// Пересечение: сужаем границы только если у item есть значение
			if (item.start !== null && result.start < item.start) {
				result.start = item.start;
			}
			if (item.end !== null && result.end > item.end) {
				result.end = item.end;
			}

			return result;
		}, null);
	}

	/**
	 * Форматирование Decimal до фиксированного scale с разделителями разрядов.
	 * Ядро человекочитаемого представления Numeric — перенесено из старого
	 * Numeric.format. Источник — toFixed(scale), а не number.toString, поэтому
	 * точность и число знаков детерминированы (хвостовые нули видны:
	 * scale=2 → "5.00", scale=3 → "5.000").
	 *
	 * Только число: валюта и единица измерения — забота денежных/количественных
	 * наследников Numeric, они клеят постфиксы поверх этого результата.
	 * Разделитель целой части — пробел каждые три разряда (минус не цифра,
	 * регэкспу не мешает).
	 *
	 * @param value - значение (Decimal из Numeric)
	 * @param scale - число знаков после запятой
	 */
	static formatDecimal(value: Decimal, scale: number): string {
		const fixed = value.toFixed(scale);
		const [integerPart, decimalPart] = fixed.split('.');
		// пробел каждые три разряда в целой части (минус не цифра — не мешает)
		const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '\u00A0');

		let result: string;
		if (decimalPart !== undefined) {
			result = `${formattedInteger}.${decimalPart}`;
		} else {
			result = formattedInteger;
		}

		return result;
	}

	/**
	 * «Умное» форматирование Decimal: без хвостовых нулей, с разделителем
	 * разрядов и дробной частью по правилам локали. Знаков после запятой —
	 * не больше scale типа (для суммы 2, для количества 3).
	 *
	 * Тело перенесено из RuUtils.formatDecimalSmart: региональных различий
	 * в нём нет, локаль — только параметр Intl. RuUtils оставил прокси.
	 *
	 * toNumber допустим: это вывод, а не расчёт; значения в пределах
	 * безопасного целого number с запасом.
	 *
	 * @param value  - значение (Decimal из Numeric)
	 * @param scale  - максимум знаков после запятой
	 * @param locale - локаль (ru_RU и ru-RU равноправны)
	 */
	static formatDecimalSmart(value: Decimal, scale: number, locale: string = 'ru-RU'): string {
		const normalizedLocale = locale.replace('_', '-');

		const formatter = new Intl.NumberFormat(normalizedLocale, {
			style:                 'decimal',
			minimumFractionDigits: 0,
			maximumFractionDigits: scale,
		});

		return formatter.format(value.toNumber());
	}

	/**
	 * Порядки для сокращённого вывода: тысячи, миллионы, миллиарды.
	 * Крупнее миллиарда не идём: «12 345 млрд» читается, а триллионы
	 * для наших сумм не встречаются.
	 */
	private static readonly SHORTEN_DIVISORS: Decimal[] = [
		new Decimal(1_000),
		new Decimal(1_000_000),
		new Decimal(1_000_000_000),
	];

	/**
	 * Подписи порядков по языку локали, в том же порядке, что SHORTEN_DIVISORS.
	 * separator — между числом и подписью: в русском неразрывный пробел
	 * («12,5 тыс»), в английском слитно («12.5K»).
	 * «тыс» без точки — короче и единообразно с «млн» и «млрд».
	 * Языка нет в таблице — берётся en.
	 */
	private static readonly SHORTEN_SUFFIXES: Record<string, { suffixes: string[]; separator: string }> = {
		ru: { suffixes: ['тыс', 'млн', 'млрд'], separator: '\u00A0' },
		en: { suffixes: ['K', 'M', 'B'],        separator: '' },
	};

	/** Знаков после запятой у сокращённого значения: 1,23 млн, 12,5 тыс */
	private static readonly SHORTEN_FRACTION_DIGITS = 2;

	/**
	 * Сокращённое форматирование Decimal: 12 500 → 12,5 тыс, 3 450 000 → 3,45 млн,
	 * 12 345 000 000 → 12,35 млрд. Меньше тысячи — как formatDecimalSmart.
	 * Подписи порядков — по языку локали (см. SHORTEN_SUFFIXES).
	 *
	 * После деления — не больше двух знаков, хвостовые нули отбрасываются
	 * (1 000 000 → 1 млн). Округление half-up, как в самих типах. Знак сохраняется.
	 *
	 * Если округление дотягивает значение до следующего порядка
	 * (999 999 → 1 000 тыс), берётся следующий порядок: 1 млн.
	 *
	 * Единицу или валюту вызывающий добавляет сам, через неразрывный
	 * пробел: «12,5 тыс ₽».
	 *
	 * @param value  - значение (Decimal из Numeric)
	 * @param scale  - максимум знаков для значений меньше тысячи
	 * @param locale - локаль (ru_RU и ru-RU равноправны)
	 */
	static formatDecimalShorten(value: Decimal, scale: number, locale: string = 'ru-RU'): string {
		let tierIndex = -1;

		for (let i = 0; i < NumberUtils.SHORTEN_DIVISORS.length; i++) {
			if (value.abs().greaterThanOrEqualTo(NumberUtils.SHORTEN_DIVISORS[i]) === true) {
				tierIndex = i;
			}
		}

		if (tierIndex === -1) {
			return NumberUtils.formatDecimalSmart(value, scale, locale);
		} else {
			let shortened = value
				.dividedBy(NumberUtils.SHORTEN_DIVISORS[tierIndex])
				.toDecimalPlaces(NumberUtils.SHORTEN_FRACTION_DIGITS, Decimal.ROUND_HALF_UP);

			// 999 999 → 1 000 тыс: округление дотянуло до следующего порядка
			if (shortened.abs().greaterThanOrEqualTo(1000) === true && tierIndex < NumberUtils.SHORTEN_DIVISORS.length - 1) {
				tierIndex = tierIndex + 1;
				shortened = value
					.dividedBy(NumberUtils.SHORTEN_DIVISORS[tierIndex])
					.toDecimalPlaces(NumberUtils.SHORTEN_FRACTION_DIGITS, Decimal.ROUND_HALF_UP);
			}

			const language = locale.replace('_', '-').split('-')[0].toLowerCase();
			let names = NumberUtils.SHORTEN_SUFFIXES[language];

			if (names === undefined) {
				names = NumberUtils.SHORTEN_SUFFIXES.en;
			}

			return NumberUtils.formatDecimalSmart(shortened, NumberUtils.SHORTEN_FRACTION_DIGITS, locale)
				+ names.separator
				+ names.suffixes[tierIndex];
		}
	}

	/**
	 * Универсальное форматирование чисел
	 * @param value - Число для форматирования
	 * @param decimals - Максимальное количество знаков после запятой (по умолчанию 0)
	 * @param hideTrailingZeros - Не показывать дробную часть для целых чисел (по умолчанию false)
	 * @param locale - Локаль для форматирования (по умолчанию 'ru-RU')
	 */
	static numberFormat(
		value: unknown,
		decimals: number = 0,
		hideTrailingZeros: boolean = false,
		locale: string = 'ru-RU'
	): string | null {
		value = vueUnref(value);

		// Преобразуем значение в число
		let numericValue: number | null = null;

		if (this.isNumber(value)) {
			numericValue = value;
		} else if (StringUtils.isString(value)) {
			// Заменяем запятые на точки для парсинга
			const cleanValue = value.replace(/,+/g, '.');
			const parsed = parseFloat(cleanValue);
			numericValue = isNaN(parsed) ? null : parsed;
		}

		if (numericValue === null) {
			return null;
		}

		// Используем стандартный Intl.NumberFormat
		const formatOptions: Intl.NumberFormatOptions = {
			minimumFractionDigits: hideTrailingZeros ? 0 : decimals,
			maximumFractionDigits: decimals,
			style: 'decimal'
		};

		return new Intl.NumberFormat(locale, formatOptions).format(numericValue);
	}

	/**
	 * Валидация вводимого количества.
	 * Возвращает текст ошибки или null.
	 *
	 * Принимает Qty по всем количественным аргументам (типобезопасная граница),
	 * но ВНУТРИ разворачивает в number: это UX-валидация ввода (кратность фасовке,
	 * мин/макс), а не денежный расчёт — точность Decimal здесь не нужна.
	 *
	 * «Не введено» (desiredQty === null либо значение объекта невалидно —
	 * Qty.isEmpty()) — не ошибка, возвращается null: отсутствие ввода валидатор
	 * не бракует, это забота required-проверки контрола.
	 *
	 * @param desiredQty    - вводимое значение (null = не введено)
	 * @param packageVolume - объём фасовки (для проверки кратности)
	 * @param maxQty        - максимум, больше которого заказать нельзя
	 * @param minQty        - минимум, меньше которого заказать нельзя
	 * @param unit          - единица измерения (объект с полем name)
	 * @param isRawMaterialsAndPackage - валидировать фасовку только для сырья и упаковки
	 */
	static validateDesiredQty(
		desiredQty: Qty | null,
		packageVolume?: Qty | null,
		maxQty?: Qty,
		minQty?: Qty,
		unit?: { name?: string },
		// аргумент стоит последним, потому что false это редкий кейс
		isRawMaterialsAndPackage: boolean = true,
	): string | null {
		// Не введено: объекта нет либо его значение невалидно (isEmpty).
		// Отсутствие ввода не бракуем — это забота required-проверки контрола.
		if (desiredQty === null) return null;
		if (desiredQty.isEmpty()) return null;

		// Разворачиваем Qty в number один раз на входе — дальше нативная арифметика.
		const desired = desiredQty.value.toNumber();
		const pkg     = packageVolume !== null && packageVolume !== undefined ? packageVolume.value.toNumber() : null;
		const max     = maxQty !== undefined ? maxQty.value.toNumber() : undefined;
		const min     = minQty !== undefined ? minQty.value.toNumber() : undefined;

		if (desired === 0) return 'Количество не может быть равно нулю';
		if (desired < 0) return 'Введено отрицательное значение';

		const unitName = unit?.name ?? '';

		if (min != null && desired < min) {
			return `Минимум: ${min} ${unitName}`;
		}

		if (max != null && desired > max) {
			return `Максимум: ${max} ${unitName}`;
		}

		// Если товар не относится к категории Сырьё и Упаковка, то валидировать фасовку не нужно
		if (!isRawMaterialsAndPackage) return null;
		// Если фасовка не введена, то ошибку возвращать не надо
		if (!Core.utils.number.isNumber(pkg)) return null;
		if (pkg === 0 || !pkg) return 'Фасовка не может быть равна нулю или пустой';

		const ratio = NumberUtils.round10(desired / pkg, -3);
		if (Number.isInteger(ratio)) {
			return null;
		}

		// Сколько полных упаковок влезает в введённое количество
		const fullPackages = Math.floor(ratio);

		// Ближайшее допустимое значение НИЖЕ введённого
		// Math.max — чтобы не предлагать 0, минимум одна упаковка
		const start = NumberUtils.round10(
			Math.max(fullPackages * pkg, pkg),
			-3,
		);
		// Ближайшее допустимое значение ВЫШЕ введённого
		const end = NumberUtils.round10(start + pkg, -3);

		if (max != null && end > max) {
			return `Допустимое значение: ${start} ${unitName}. Количество должно быть кратно фасовке ${pkg} ${unitName}`;
		}

		return `Допустимые значения: ${start} ${unitName} или ${end} ${unitName}. Количество должно быть кратно фасовке ${pkg} ${unitName}`;
	}

	static formatWithDelimiter = StringUtils.formatWithDelimiter;
	static formatPhoneNumber = StringUtils.formatPhoneNumber;
}

export default NumberUtils;